import { useCallback, useEffect, useRef, useState } from "react";
import { CheckCircle2, FileText, Download, Trash2, Upload, AlertCircle, FileX2, MinusCircle } from "lucide-react";
import merchantApi from "../../../lib/merchantApi";
import { SelectInput, FieldShell } from "./fields";
import NAModal from "./NAModal";
import PreviousVersions from "./PreviousVersions";

/**
 * Step 6 — Supporting documents (Brief 2 §6).
 *
 * Top question drives the conditional doc visibility. Tiles render in
 * Required → Conditional (active) → Optional order, matching the brief.
 */
export default function Step6Documents({ app, onAppPatch, submitGated, stepStatus, gotoStep }) {
  const [doclist, setDoclist] = useState(null); // server response
  const [loading, setLoading] = useState(true);
  const [naFor, setNaFor] = useState(null);
  const [naSubmitting, setNaSubmitting] = useState(false);
  const [naError, setNaError] = useState("");
  const [tileError, setTileError] = useState({});  // { [docKey]: errorMsg }
  const [busy, setBusy] = useState({});            // { [docKey]: 'uploading'|'na'|'removing'|'downloading' }
  const fileInputs = useRef({});

  const refresh = useCallback(async () => {
    const { data } = await merchantApi.get("/merchant/documents");
    setDoclist(data);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try { await refresh(); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [refresh]);

  // ----- Step 6's single form field: has_prior_processor -----
  // Local state owns the radio value during a session. Server is the source
  // of truth only on initial mount; subsequent server responses NEVER write
  // back to this state (CONTRIBUTING.md "Save responses never write to
  // user-editable state"). We still tell the parent about the new step
  // metadata so the step-status pill updates inline.
  const serverAnswer = app?.step_data?.step6?.has_prior_processor;
  const [localProcessor, setLocalProcessor] = useState(serverAnswer);
  // First-load sync only — if the parent hands us a different initial answer
  // than what we have locally (e.g. user landed on step 6 after returning
  // from another step), seed once.
  const seededRef = useRef(false);
  useEffect(() => {
    if (!seededRef.current && serverAnswer !== undefined) {
      setLocalProcessor(serverAnswer);
      seededRef.current = true;
    }
  }, [serverAnswer]);

  const step6 = { has_prior_processor: localProcessor };
  const setProcessorAnswer = async (v) => {
    setLocalProcessor(v);  // optimistic — local state owns the radio
    try {
      const { data } = await merchantApi.put("/merchant/application/step/6", {
        data: { has_prior_processor: v },
      });
      // STRICT: forward metadata only — never `step_data`. Note: step PUT
      // responses never include `schemas` (only the bootstrap GET does), so
      // do NOT echo `data?.schemas` here — spreading `schemas: undefined`
      // would clobber the parent's already-loaded schema map and break
      // form rendering on every other step (Bug 1 — Feb 2026).
      onAppPatch?.({
        application_id: data?.application_id, ref: data?.ref,
        status: data?.status, step_status: data?.step_status,
        last_step: data?.last_step,
        last_saved_at: data?.last_saved_at,
      });
      await refresh(); // conditional doc may now activate / deactivate
    } catch (e) { /* silent — auto-save semantics */ }
  };

  const setBusyFor = (k, v) => setBusy((b) => ({ ...b, [k]: v }));
  const setError = (k, msg) => setTileError((e) => ({ ...e, [k]: msg }));

  const handleUpload = async (docKey, file) => {
    setError(docKey, "");
    setBusyFor(docKey, "uploading");
    const fd = new FormData();
    fd.append("file", file);
    try {
      await merchantApi.post(`/merchant/documents/${docKey}/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await refresh();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(docKey, typeof detail === "string" ? detail : "Upload failed.");
    } finally {
      setBusyFor(docKey, null);
    }
  };

  const handleSubmitNA = async (justification) => {
    if (!naFor) return;
    setNaSubmitting(true); setNaError("");
    try {
      await merchantApi.post(`/merchant/documents/${naFor.key}/na`, { justification });
      await refresh();
      setNaFor(null);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setNaError(typeof detail === "string" ? detail : "Could not save the justification.");
    } finally {
      setNaSubmitting(false);
    }
  };

  const handleRemove = async (docKey) => {
    setError(docKey, "");
    setBusyFor(docKey, "removing");
    try {
      await merchantApi.delete(`/merchant/documents/${docKey}`);
      await refresh();
    } finally {
      setBusyFor(docKey, null);
    }
  };

  const handleDownload = async (docId, filename) => {
    try {
      const { data } = await merchantApi.post(`/merchant/documents/${docId}/url`);
      // Use bearer-driven fetch + blob to handle cookie-blocked browsers.
      const token = localStorage.getItem("neptune_merchant_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const resp = await fetch(data.url, { credentials: "include", headers });
      if (!resp.ok) throw new Error("download failed");
      const blob = await resp.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = filename || "document";
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 30000);
    } catch (err) { /* silent */ }
  };

  if (loading || !doclist) {
    return <div data-testid="step6-loading" className="text-zinc-500 text-[12px] uppercase tracking-[0.22em]">Loading documents…</div>;
  }

  const required = doclist.items.filter((it) => it.tier === "required");
  const conditional = doclist.items.filter((it) => it.tier === "conditional");
  const optional = doclist.items.filter((it) => it.tier === "optional");
  const conditionalActive = conditional.filter((it) => it.active);

  return (
    <div data-testid="step6-page" className="space-y-7">
      {/* Brief 3.10 — pending replacement requests + recent rejections
          from the AM. Shown before any doc tiles so the merchant
          knows what to fix first. */}
      <PendingReplacementRequests merchantApi={merchantApi} />

      {/* Top question — drives the conditional doc */}
      <div className="rounded-3xl border border-white/[0.06] bg-white/[0.015] p-6">
        <FieldShell
          label="Do you have a current or prior payment processor?"
          required
          testId="step6-has-processor"
          hint="Answering Yes adds the Processing statements (last 3 months) tile below."
        >
          <SelectInput
            value={step6.has_prior_processor || ""}
            onChange={setProcessorAnswer}
            options={["Yes", "No"]}
            testId="step6-has-processor-input"
          />
        </FieldShell>
      </div>

      {/* Submit-readiness banner — reflects the combined gate (docs + form
          completion across steps 1..5). Bug 2 fix (Feb 2026): previously this
          banner only checked doclist.submit_ready, so it could falsely tell
          a merchant "you can submit" while steps 1/2/3 were still partial. */}
      {(() => {
        const incompleteSteps = [1, 2, 3, 4, 5].filter(
          (n) => (stepStatus?.[String(n)] || "incomplete") !== "complete"
        );
        const docsReady = !!doclist.submit_ready;
        const allReady = docsReady && incompleteSteps.length === 0;
        const onlyDocsMissing = !docsReady && incompleteSteps.length === 0;
        const onlyStepsMissing = docsReady && incompleteSteps.length > 0;

        const tone = allReady
          ? "border-emerald-400/20 bg-emerald-400/[0.04] text-emerald-200"
          : "border-amber-400/20 bg-amber-400/[0.04] text-amber-200";
        const Icon = allReady ? CheckCircle2 : AlertCircle;

        return (
          <div
            data-testid="step6-readiness"
            data-state={allReady ? "ready" : onlyDocsMissing ? "docs-missing" : onlyStepsMissing ? "steps-missing" : "both-missing"}
            className={`rounded-2xl border px-5 py-3.5 text-[12.5px] flex items-start gap-2.5 ${tone}`}
          >
            <Icon className="h-3.5 w-3.5 mt-[3px] shrink-0" />
            <div className="flex-1 min-w-0 leading-relaxed">
              {allReady && "All set — you can submit your application from step 7."}
              {onlyDocsMissing && "Upload or mark N/A on all required documents to submit."}
              {onlyStepsMissing && (
                <>
                  Almost ready — finish{" "}
                  {incompleteSteps.map((n, i) => (
                    <span key={n}>
                      <button
                        type="button"
                        onClick={() => gotoStep?.(n)}
                        data-testid={`step6-readiness-jump-${n}`}
                        className="underline underline-offset-2 hover:text-amber-100 transition-colors"
                      >
                        step {n}
                      </button>
                      {i < incompleteSteps.length - 2 ? ", " : i === incompleteSteps.length - 2 ? " and " : ""}
                    </span>
                  ))}
                  {" "}before submitting.
                </>
              )}
              {!allReady && !onlyDocsMissing && !onlyStepsMissing && (
                <>
                  Two things left — upload or mark N/A on the remaining documents, and finish{" "}
                  {incompleteSteps.map((n, i) => (
                    <span key={n}>
                      <button
                        type="button"
                        onClick={() => gotoStep?.(n)}
                        data-testid={`step6-readiness-jump-${n}`}
                        className="underline underline-offset-2 hover:text-amber-100 transition-colors"
                      >
                        step {n}
                      </button>
                      {i < incompleteSteps.length - 2 ? ", " : i === incompleteSteps.length - 2 ? " and " : ""}
                    </span>
                  ))}
                  .
                </>
              )}
            </div>
          </div>
        );
      })()}

      {/* Required */}
      <Section title={`Required (${required.length})`} subtitle="Marked with a red *">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {required.map((it) => (
            <DocTile
              key={it.key} doc={it}
              busy={busy[it.key]} error={tileError[it.key]}
              onUploadClick={() => fileInputs.current[it.key]?.click()}
              onMarkNA={() => { setNaError(""); setNaFor({ key: it.key, label: it.label, initial: it.current?.na_justification || "" }); }}
              onRemove={() => handleRemove(it.key)}
              onDownload={handleDownload}
              maxFileMb={doclist.max_file_mb}
              fileRef={(el) => (fileInputs.current[it.key] = el)}
              onFileChosen={(f) => f && handleUpload(it.key, f)}
            />
          ))}
        </div>
      </Section>

      {/* Conditional (active only) */}
      {conditionalActive.length > 0 && (
        <Section title={`Conditional (${conditionalActive.length})`} subtitle="Marked with a yellow *">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {conditionalActive.map((it) => (
              <DocTile
                key={it.key} doc={it} conditional
                busy={busy[it.key]} error={tileError[it.key]}
                onUploadClick={() => fileInputs.current[it.key]?.click()}
                onMarkNA={() => { setNaError(""); setNaFor({ key: it.key, label: it.label, initial: it.current?.na_justification || "" }); }}
                onRemove={() => handleRemove(it.key)}
                onDownload={handleDownload}
                maxFileMb={doclist.max_file_mb}
                fileRef={(el) => (fileInputs.current[it.key] = el)}
                onFileChosen={(f) => f && handleUpload(it.key, f)}
              />
            ))}
          </div>
        </Section>
      )}

      {/* Optional */}
      <div className="border-t border-white/[0.06] pt-7">
        <Section title="Optional" subtitle="No asterisk — won't block submission.">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {optional.map((it) => (
              <DocTile
                key={it.key} doc={it} optional
                busy={busy[it.key]} error={tileError[it.key]}
                onUploadClick={() => fileInputs.current[it.key]?.click()}
                onRemove={() => handleRemove(it.key)}
                onDownload={handleDownload}
                maxFileMb={doclist.max_file_mb}
                fileRef={(el) => (fileInputs.current[it.key] = el)}
                onFileChosen={(f) => f && handleUpload(it.key, f)}
              />
            ))}
          </div>
        </Section>
      </div>

      <NAModal
        open={!!naFor}
        onClose={() => !naSubmitting && setNaFor(null)}
        docLabel={naFor?.label}
        initial={naFor?.initial}
        minLength={doclist.na_min_length}
        maxLength={doclist.na_max_length}
        onSubmit={handleSubmitNA}
        submitting={naSubmitting}
        error={naError}
      />
    </div>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <section>
      <div className="flex items-baseline justify-between mb-3">
        <h3 className="font-display text-[18px] font-semibold tracking-tight text-white">{title}</h3>
        <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-500">{subtitle}</div>
      </div>
      {children}
    </section>
  );
}

function DocTile({
  doc, busy, error,
  conditional, optional,
  onUploadClick, onMarkNA, onRemove, onDownload,
  maxFileMb, fileRef, onFileChosen,
}) {
  const cur = doc.current;
  const tierBadge = doc.tier === "required"
    ? <span className="text-rose-300">*</span>
    : doc.tier === "conditional"
      ? <span className="text-amber-300">*</span>
      : <span className="text-[10.5px] uppercase tracking-[0.18em] text-zinc-500 ml-1">Optional</span>;

  const stateChip =
    cur?.state === "uploaded" ? (
      <span data-testid={`tile-state-${doc.key}`} className="inline-flex items-center gap-1 text-[11px] text-emerald-300">
        <CheckCircle2 className="h-3 w-3" /> Uploaded
      </span>
    ) : cur?.state === "na" ? (
      <span data-testid={`tile-state-${doc.key}`} className="inline-flex items-center gap-1 text-[11px] text-amber-300">
        <MinusCircle className="h-3 w-3" /> Marked N/A
      </span>
    ) : (
      <span data-testid={`tile-state-${doc.key}`} className="inline-flex items-center gap-1 text-[11px] text-zinc-500">
        <FileX2 className="h-3 w-3" /> Empty
      </span>
    );

  return (
    <div
      data-testid={`doc-tile-${doc.key}`}
      className="rounded-2xl border border-white/[0.06] bg-white/[0.015] hover:bg-white/[0.025] p-4 transition-colors"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[13.5px] text-white leading-snug font-medium">
            {doc.label}{tierBadge && doc.tier !== "optional" && <span className="ml-1">{tierBadge}</span>}
          </div>
          {doc.tier === "optional" && (
            <div className="mt-0.5 text-[10.5px] uppercase tracking-[0.18em] text-zinc-500">Optional</div>
          )}
          {conditional && doc.condition_help && (
            <div className="mt-1 text-[11.5px] text-zinc-500">{doc.condition_help}</div>
          )}
          <div className="mt-2">{stateChip}</div>
          {cur?.state === "uploaded" && (
            <div className="mt-1 flex items-center gap-2 text-[11.5px] text-zinc-400 truncate">
              <FileText className="h-3 w-3 shrink-0" />
              <span className="truncate" title={cur.filename}>{cur.filename}</span>
              <span className="text-zinc-600">· {(cur.size / (1024 * 1024)).toFixed(2)} MB</span>
            </div>
          )}
          {cur?.state === "na" && (
            <div className="mt-1 text-[11.5px] text-zinc-400 line-clamp-2" title={cur.na_justification}>
              "{cur.na_justification}"
            </div>
          )}
        </div>
      </div>

      <div className="mt-3 flex items-center gap-2 flex-wrap">
        <input
          type="file"
          ref={fileRef}
          onChange={(e) => onFileChosen(e.target.files?.[0])}
          accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
          className="hidden"
          data-testid={`tile-file-input-${doc.key}`}
        />
        {!cur && (
          <>
            <button
              type="button"
              onClick={onUploadClick}
              disabled={busy === "uploading"}
              data-testid={`tile-upload-${doc.key}`}
              className="inline-flex items-center gap-1.5 rounded-full border border-white/[0.10] bg-white/[0.02] hover:bg-white/[0.06] text-white px-3 py-1.5 text-[11.5px] disabled:opacity-50"
            >
              <Upload className="h-3 w-3" /> {busy === "uploading" ? "Uploading…" : "Upload"}
            </button>
            {!optional && (
              <button
                type="button"
                onClick={onMarkNA}
                data-testid={`tile-na-${doc.key}`}
                className="inline-flex items-center gap-1.5 rounded-full border border-amber-400/20 bg-amber-400/[0.04] hover:bg-amber-400/[0.08] text-amber-200 px-3 py-1.5 text-[11.5px]"
              >
                Mark N/A
              </button>
            )}
          </>
        )}
        {cur && cur.state === "uploaded" && (
          <>
            <button
              type="button"
              onClick={() => onDownload(cur.doc_id, cur.filename)}
              data-testid={`tile-download-${doc.key}`}
              className="inline-flex items-center gap-1.5 rounded-full border border-white/[0.10] bg-white/[0.02] hover:bg-white/[0.06] text-white px-3 py-1.5 text-[11.5px]"
            >
              <Download className="h-3 w-3" /> Download
            </button>
            <button
              type="button"
              onClick={onUploadClick}
              data-testid={`tile-replace-${doc.key}`}
              className="inline-flex items-center gap-1.5 rounded-full border border-white/[0.10] bg-white/[0.02] hover:bg-white/[0.06] text-white px-3 py-1.5 text-[11.5px]"
            >
              Replace
            </button>
            <button
              type="button"
              onClick={onRemove}
              data-testid={`tile-remove-${doc.key}`}
              disabled={busy === "removing"}
              className="inline-flex items-center gap-1.5 rounded-full border border-white/[0.10] bg-white/[0.02] hover:bg-rose-500/[0.10] hover:text-rose-200 text-zinc-400 px-3 py-1.5 text-[11.5px] disabled:opacity-50"
            >
              <Trash2 className="h-3 w-3" /> Remove
            </button>
          </>
        )}
        {cur && cur.state === "na" && (
          <>
            <button
              type="button"
              onClick={onMarkNA}
              data-testid={`tile-edit-na-${doc.key}`}
              className="inline-flex items-center gap-1.5 rounded-full border border-amber-400/20 bg-amber-400/[0.04] hover:bg-amber-400/[0.08] text-amber-200 px-3 py-1.5 text-[11.5px]"
            >
              Edit N/A
            </button>
            <button
              type="button"
              onClick={onUploadClick}
              data-testid={`tile-upload-instead-${doc.key}`}
              className="inline-flex items-center gap-1.5 rounded-full border border-white/[0.10] bg-white/[0.02] hover:bg-white/[0.06] text-white px-3 py-1.5 text-[11.5px]"
            >
              <Upload className="h-3 w-3" /> Upload instead
            </button>
            <button
              type="button"
              onClick={onRemove}
              data-testid={`tile-remove-${doc.key}`}
              disabled={busy === "removing"}
              className="inline-flex items-center gap-1.5 rounded-full border border-white/[0.10] bg-white/[0.02] hover:bg-white/[0.06] text-zinc-400 px-3 py-1.5 text-[11.5px] disabled:opacity-50"
            >
              <Trash2 className="h-3 w-3" /> Clear
            </button>
          </>
        )}
      </div>

      {error && (
        <div data-testid={`tile-error-${doc.key}`} className="mt-3 rounded-lg border border-rose-400/20 bg-rose-400/[0.05] text-rose-200 text-[11.5px] px-3 py-2">
          {error}
        </div>
      )}

      <PreviousVersions docKey={doc.key} count={doc.previous_versions_count} onDownload={onDownload} />

      {!cur && doc.tier === "required" && (
        <div className="mt-3 text-[10.5px] text-zinc-600">PDF / DOC / XLS / JPG / PNG · max {maxFileMb} MB per file</div>
      )}
    </div>
  );
}

// Brief 3.10 — pending replacement requests + recent rejections.
function PendingReplacementRequests({ merchantApi }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data: body } = await merchantApi.get("/merchant/documents/pending-requests");
        if (alive) setData(body);
      } catch { /* silent — empty card if endpoint fails */ }
    })();
    return () => { alive = false; };
  }, [merchantApi]);

  if (!data) return null;
  const pending = data.pending || [];
  const rejections = data.recent_rejections || [];
  if (!pending.length && !rejections.length) return null;

  return (
    <div
      data-testid="merchant-doc-pending-requests"
      className="rounded-3xl border border-amber-400/20 bg-amber-400/[0.04] p-5"
    >
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-amber-300">
        <AlertCircle className="h-3.5 w-3.5" />
        Action requested by your account manager
      </div>
      <div className="mt-3 space-y-2.5">
        {pending.map((p) => (
          <div key={p.request_id} className="rounded-md border border-amber-400/20 bg-black/20 p-3" data-testid={`merchant-pending-${p.doc_key}`}>
            <div className="text-[12px] uppercase tracking-wider text-amber-200">{p.doc_key.replace(/_/g, " ")}</div>
            <div className="mt-1 text-[12.5px] text-zinc-200 leading-relaxed">{p.instruction}</div>
          </div>
        ))}
        {rejections.map((r) => (
          <div key={r.doc_id} className="rounded-md border border-rose-400/20 bg-rose-400/[0.04] p-3" data-testid={`merchant-rejection-${r.doc_id}`}>
            <div className="text-[11.5px] text-rose-300">
              Your last upload to <span className="font-medium">{r.doc_key.replace(/_/g, " ")}</span> ({r.filename}) was rejected:
            </div>
            <div className="mt-1 text-[12.5px] text-rose-100 leading-relaxed">{r.rejection_reason}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

